
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface LoadingStateProps {
  text?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'dots' | 'spinner' | 'pulse';
  className?: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({
  text = 'Loading...',
  size = 'medium',
  variant = 'dots',
  className
}) => {
  const sizeClasses = {
    small: 'space-x-1',
    medium: 'space-x-2',
    large: 'space-x-3'
  };
  
  const dotSizes = {
    small: 'w-2 h-2',
    medium: 'w-3 h-3',
    large: 'w-4 h-4'
  };
  
  const spinnerSizes = {
    small: 'w-5 h-5',
    medium: 'w-10 h-10',
    large: 'w-16 h-16'
  };
  
  const textSizes = {
    small: 'text-xs',
    medium: 'text-base',
    large: 'text-xl'
  };
  
  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      {variant === 'dots' && (
        <div className={cn("flex justify-center", sizeClasses[size], "mb-4")}>
          <motion.div
            className={cn(dotSizes[size], "bg-purple-500 rounded-full")}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0 }}
          />
          <motion.div
            className={cn(dotSizes[size], "bg-purple-500 rounded-full")}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
          />
          <motion.div
            className={cn(dotSizes[size], "bg-purple-500 rounded-full")}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          />
        </div>
      )}
      
      {variant === 'spinner' && (
        <div className="relative mb-4">
          <motion.div
            className={cn(spinnerSizes[size], "border-4 border-[#6C41E0]/30 border-t-[#6C41E0] rounded-full")}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      )}
      
      {variant === 'pulse' && (
        <div className="relative mb-4">
          <motion.div
            className={cn(spinnerSizes[size], "rounded-full bg-[#6C41E0]/20")}
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className={cn(spinnerSizes[size], "absolute top-0 left-0 rounded-full bg-[#6C41E0]/10")}
            animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          />
        </div>
      )}
      
      {text && (
        <motion.p
          className={cn("text-white/80 font-medium", textSizes[size])}
          initial={{ opacity: 0.8 }}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

export default LoadingState;
